import type { ChangeEvent, FocusEvent, HTMLProps, ReactNode } from "react";
import { useRef } from "react";
import { Input, Label, TextField } from "react-aria-components";
import type { TextFieldProps } from "react-aria-components";
import { useMergeRefs } from "@floating-ui/react";
import { cn } from "../../utilities/tailwind";

export type BaseInputTextProperties = {
  /**
   * Class name to apply to the outer container of the InputText
   */
  className?: string | undefined;

  /**
   * Class name to apply to the input element
   */
  inputClassName?: string | undefined;

  /**
   * HTML properties to pass along to the input element
   */
  inputProperties?: HTMLProps<HTMLInputElement>;

  /**
   * Relevant description of the purpose of the input element.
   */
  label: string;

  /**
   * Class name to apply to the label element
   */
  labelClassName?: string | undefined;

  /**
   * Function called when user input changes the text field
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>, value: string) => void;

  /**
   * Should the text in the input be selected on focus
   */
  selectOnFocus?: boolean | undefined;

  /**
   * The value of the input element
   */
  value?: string | undefined;

  /**
   * Predetermined stylings for the InputText
   */
  variant?: "primary" | "secondary" | undefined;

  error?: ReactNode | undefined;
};
export type InputTextProperties = Omit<
  TextFieldProps,
  "children" | "className" | "defaultValue" | "onChange" | "slot" | "style"
> &
  BaseInputTextProperties;
export const InputText = ({
  className = "",
  inputClassName = "",
  inputProperties: { ref: inputReference, ...inputProperties } = {},
  label,
  labelClassName = "",
  onChange,
  onFocus,
  selectOnFocus,
  value,
  variant = "primary",
  error,
  ...properties
}: InputTextProperties) => {
  const localReference = useRef<HTMLInputElement>(null);
  const reference = useMergeRefs([inputReference, localReference]);
  const twTexFieldClassName = cn(
    "relative flex h-14 w-full border border-outline bg-surface-container group-data-[disabled=true]/text-field:bg-disabled group-data-[invalid=true]/text-field:border-status-negative focus-within:border-primary",
    { "mt-7": variant === "secondary" },
    className,
  );
  const twInputClassName = cn(
    "peer w-full rounded-none border border-transparent pt-6 pr-2 pb-2 pl-4 font-sans text-body text-on-surface outline-none group-data-[disabled=true]/text-field:text-disabled-variant focus:border-primary focus:data-[invalid=true]:border-status-negative",
    { "pt-2": variant === "secondary" },
    inputClassName,
  );
  const twLabelClassName = cn(
    {
      "absolute -top-7 text-body": variant === "secondary",
      [`pointer-events-none absolute m-4 font-sans text-[16px] text-on-surface-variant-medium transition-all group-data-[disabled=true]/text-field:text-disabled-variant peer-focus:mt-2 peer-focus:text-[12px] peer-focus:text-on-surface peer-[:not(:placeholder-shown)]:mt-2 peer-[:not(:placeholder-shown)]:text-[12px] peer-[:not(:placeholder-shown)]:text-on-surface`]:
        variant === "primary",
    },
    labelClassName,
  );

  const onChangeHandle = onChange
    ? (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event, event.target.value);
      }
    : undefined;

  const handleFocus = selectOnFocus
    ? (event: FocusEvent<HTMLInputElement>) => {
        onFocus?.(event);
        requestAnimationFrame(() => localReference.current?.select());
      }
    : onFocus;

  return (
    <TextField
      onFocus={handleFocus}
      {...properties}
      className="group/text-field w-full"
    >
      <div className={twTexFieldClassName}>
        <Input
          className={twInputClassName}
          onChange={onChangeHandle}
          placeholder=" "
          ref={reference}
          value={value}
          {...inputProperties}
        />
        <Label className={twLabelClassName}>{label}</Label>
      </div>
      {error}
    </TextField>
  );
};
InputText.displayName = "InputText";
